import React from 'react';
import { observer } from 'mobx-react-lite';
import { Box } from '@mantine/core';
import { Collection } from '@common/merch';
import { CategorySelectors } from '../category/CategorySelectors';
import { SidebarCategorySkeleton } from '../loader/SidebarCategorySkeleton';
import { SidebarOverlay } from './SidebarOverlay';
import { SidebarHeader } from './SidebarHeader';
import { MobileSidebarNav } from '../layout/MobileSidebarNav';
import { useSidebarStyle } from './useSidebarStyle';
import { useSidebarBodyLock } from './useSidebarBodyLock';
import { Z_INDEX } from '../layout/LayoutConstants';
import { useStore } from '../../state';
import { REACT_APP_DISCORD_INVITE, REACT_APP_MINECRAFT_IP } from '../../views/store/StoreController';
import { getRuntimeEnvironment } from '@trappedmc/docker-frontend-runtime-env';

interface SidebarProps {
  collections: Collection[];
  activeCollection: string | null;
  onCollectionClick: (collectionId: string, collectionName?: string) => void;
  playersOnline?: number;
  discordOnline?: number;
  isMobile?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
  isLoading?: boolean;
}

const { REACT_APP_FULL_URL = '' } = getRuntimeEnvironment();

export const Sidebar: React.FC<SidebarProps> = observer(
  ({
    collections,
    activeCollection,
    onCollectionClick,
    playersOnline = 0,
    discordOnline = 0,
    isMobile = false,
    isOpen = false,
    onClose,
    isLoading = false
  }) => {
    const rootStore = useStore();
    const sidebarStyle = useSidebarStyle({ isMobile, isOpen });

    useSidebarBodyLock(isMobile, isOpen);

    const handleCollectionClick = (collectionId: string, collectionName?: string) => {
      onCollectionClick(collectionId, collectionName);
      if (isMobile && onClose) {
        onClose();
      }
    };

    const handleServerClick = async () => {
      try {
        await navigator.clipboard.writeText(REACT_APP_MINECRAFT_IP);
        rootStore.alertStore.showAlert(`Copied ${REACT_APP_MINECRAFT_IP} to clipboard!`, 'success');
      } catch (error) {
        console.error('Failed to copy IP:', error);
        rootStore.alertStore.showAlert('Failed to copy IP address', 'error');
      }
    };

    const handleDiscordClick = () => {
      window.open(REACT_APP_DISCORD_INVITE, '_blank', 'noopener,noreferrer');
    };

    return (
      <>
        <SidebarOverlay visible={isMobile && isOpen} onClick={onClose!} zIndex={Z_INDEX.OVERLAY} />

        <Box style={sidebarStyle} className='custom-scroller'>
          {isMobile && <MobileSidebarNav onNavigate={onClose} />}

          {!isMobile && (
            <SidebarHeader
              playersOnline={playersOnline}
              discordOnline={discordOnline}
              logoUrl={`${REACT_APP_FULL_URL}/assets/images/logo.png`}
              isMinecraftLoading={rootStore.storeStore.isMinecraftLoading}
              isDiscordLoading={rootStore.storeStore.isDiscordLoading}
              onServerClick={handleServerClick}
              onDiscordClick={handleDiscordClick}
            />
          )}

          {isLoading ? (
            <SidebarCategorySkeleton count={5} />
          ) : (
            <CategorySelectors
              collections={collections}
              activeCollection={activeCollection}
              onCollectionClick={handleCollectionClick}
            />
          )}
        </Box>
      </>
    );
  }
);
